import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import GenerateButton from "./GenerateButton";

export default function TemplateDropdown({
  templates = [],
  selectedTemplateIds = [],
  onToggleTemplate,
  onGenerate,
  loading = false,
  disabled = false,
  generatedTemplateIds = [],
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [buttonPosition, setButtonPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const desktopDropdownRef = useRef(null); // portal content (desktop)
  const mobileRef = useRef(null); // portal content (mobile)
  const buttonRef = useRef(null); // trigger button
  const wrapperRef = useRef(null); // local wrapper (not used for outside click)

  // Calculate button position for portal positioning
  const updateButtonPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 320; // w-80
      const viewportLeftPadding = 16; // keep away from the absolute edge
      const maxLeft =
        window.innerWidth -
        dropdownWidth -
        viewportLeftPadding +
        window.scrollX;
      const desiredLeft = rect.right - dropdownWidth + window.scrollX; // right-align to button
      setButtonPosition({
        top: rect.bottom + window.scrollY + 8,
        left: Math.max(
          viewportLeftPadding + window.scrollX,
          Math.min(desiredLeft, maxLeft)
        ),
        width: rect.width,
      });
    }
  };

  // Handle dropdown toggle with position update
  const handleToggleDropdown = () => {
    if (!dropdownOpen) {
      updateButtonPosition();
    }
    setDropdownOpen(!dropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const onDocClick = (e) => {
      const clickedInsideDesktop = desktopDropdownRef.current?.contains?.(
        e.target
      );
      const clickedInsideMobile = mobileRef.current?.contains?.(e.target);
      const clickedButton = buttonRef.current?.contains?.(e.target);
      if (!clickedInsideDesktop && !clickedInsideMobile && !clickedButton) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [dropdownOpen]);

  // Close dropdown when generation starts
  useEffect(() => {
    if (loading) {
      setDropdownOpen(false);
    }
  }, [loading]);

  // Wrap onGenerate to close dropdown
  const handleGenerate = async () => {
    setDropdownOpen(false);
    if (onGenerate) {
      await onGenerate();
    }
  };

  return (
    <div className="relative mr-2 sm:mr-0" ref={wrapperRef}>
      {/* Premium Dropdown Button */}
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        disabled={loading}
        className={`group relative inline-flex items-center gap-3 px-4 py-2.5 text-sm font-semibold transition-all duration-200 rounded-xl overflow-hidden ${
          selectedTemplateIds.length > 0
            ? "text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30"
            : "text-gray-700 bg-white hover:bg-gray-50 border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md"
        } focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {/* Background glow effect */}
        <div
          className={`absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
            selectedTemplateIds.length > 0 ? "opacity-20" : ""
          }`}
        />

        {/* Icon */}
        {loading ? (
          <svg
            className="animate-spin h-4 w-4 z-10"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        ) : (
          <svg
            className="w-4 h-4 z-10"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        )}

        <span className="z-10">{loading ? "Generating..." : "Templates"}</span>

        {/* Selection counter */}
        {selectedTemplateIds.length > 0 && (
          <span className="z-10 inline-flex items-center justify-center px-2 py-1 text-xs font-bold text-blue-600 bg-white rounded-full min-w-[1.5rem] animate-in zoom-in duration-200">
            {selectedTemplateIds.length}
          </span>
        )}

        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 z-10 transition-transform duration-300 ${
            dropdownOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>

        {/* Shimmer effect */}
        {selectedTemplateIds.length > 0 && (
          <div
            className="absolute inset-0 -top-[2px] -bottom-[2px] bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shimmer"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
              transform: "translateX(-100%)",
              animation: dropdownOpen ? "none" : undefined,
            }}
          />
        )}
      </button>

      {/* Portal-based dropdown for better positioning */}
      {dropdownOpen &&
        !loading &&
        createPortal(
          <>
            {/* Premium backdrop with blur */}
            <div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm sm:hidden z-[10000] animate-in fade-in duration-200"
              onClick={() => setDropdownOpen(false)}
            />

            {/* Modern glassmorphism dropdown */}
            <div
              className="hidden sm:block fixed w-80 max-h-64 z-[10001] animate-in slide-in-from-top-2 fade-in duration-300"
              style={{
                top: `${buttonPosition.top}px`,
                left: `${Math.max(16, buttonPosition.left)}px`,
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px) saturate(180%)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                borderRadius: "16px",
                boxShadow: `
                0 32px 64px -12px rgba(0, 0, 0, 0.25),
                0 0 0 1px rgba(255, 255, 255, 0.05),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `,
              }}
              ref={desktopDropdownRef}
              role="listbox"
              tabIndex={-1}
            >
              {templates.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-500 font-medium">
                    No templates available
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Templates will appear here when loaded
                  </p>
                </div>
              ) : (
                <div className="flex flex-col max-h-64">
                  {/* Header */}
                  <div className="px-6 py-4 border-b border-white/20">
                    <h3 className="text-sm font-semibold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                      Select Templates
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Choose templates to generate forms
                    </p>
                  </div>

                  {/* Scrollable template list */}
                  <div className="overflow-y-auto flex-1 max-h-40 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    {templates.map((template, index) => {
                      const isGenerated = generatedTemplateIds.includes(
                        String(template.id)
                      );
                      const isChecked = selectedTemplateIds.includes(
                        String(template.id)
                      );

                      return (
                        <label
                          key={template.id}
                          className={`group relative flex items-center px-6 py-4 transition-all duration-200 ${
                            isGenerated
                              ? "cursor-not-allowed"
                              : "cursor-pointer hover:bg-white/50 active:bg-white/70"
                          }`}
                          style={{
                            animationDelay: `${index * 50}ms`,
                          }}
                        >
                          {/* Selection indicator */}
                          <div
                            className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 ${
                              isChecked && !isGenerated
                                ? "bg-gradient-to-b from-blue-500 to-purple-600"
                                : "bg-transparent"
                            }`}
                          />

                          {/* Custom checkbox */}
                          <div className="relative mr-4">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() =>
                                !isGenerated && onToggleTemplate(template.id)
                              }
                              disabled={isGenerated}
                              className="sr-only"
                            />
                            <div
                              className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                                isGenerated
                                  ? "border-gray-300 bg-gray-100"
                                  : isChecked
                                  ? "border-blue-500 bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25"
                                  : "border-gray-300 bg-white group-hover:border-blue-400 group-hover:shadow-sm"
                              }`}
                            >
                              {isChecked && !isGenerated && (
                                <svg
                                  className="w-3 h-3 text-white animate-in zoom-in duration-200"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="3"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                              {isGenerated && (
                                <svg
                                  className="w-3 h-3 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div
                                className={`text-sm font-semibold truncate transition-colors duration-200 ${
                                  isGenerated
                                    ? "text-gray-400"
                                    : "text-gray-900 group-hover:text-gray-800"
                                }`}
                              >
                                {template.name}
                              </div>
                              {isGenerated && (
                                <span className="ml-2 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500 animate-in slide-in-from-right duration-300">
                                  <svg
                                    className="w-3 h-3 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  Generated
                                </span>
                              )}
                            </div>
                            {template.description && (
                              <div
                                className={`text-xs truncate mt-1 transition-colors duration-200 ${
                                  isGenerated
                                    ? "text-gray-400"
                                    : "text-gray-500 group-hover:text-gray-600"
                                }`}
                              >
                                {template.description}
                              </div>
                            )}
                          </div>

                          {/* Hover glow effect */}
                          {!isGenerated && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/5 to-purple-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {/* Generate Button */}
                  <GenerateButton
                    selectedTemplateIds={selectedTemplateIds}
                    onGenerate={handleGenerate}
                    disabled={disabled}
                  />
                </div>
              )}
            </div>

            {/* Mobile dropdown with premium design */}
            <div
              className="sm:hidden fixed bottom-0 left-0 right-0 max-h-[80vh] flex flex-col overscroll-contain z-[10001] animate-in slide-in-from-bottom duration-400"
              style={{
                background:
                  "linear-gradient(to top, rgba(255, 255, 255, 0.98), rgba(255, 255, 255, 0.95))",
                backdropFilter: "blur(20px) saturate(180%)",
                borderTopLeftRadius: "24px",
                borderTopRightRadius: "24px",
                boxShadow: `
                0 -32px 64px -12px rgba(0, 0, 0, 0.25),
                0 0 0 1px rgba(255, 255, 255, 0.05),
                inset 0 1px 0 rgba(255, 255, 255, 0.1)
              `,
              }}
              role="listbox"
              tabIndex={-1}
              ref={mobileRef}
            >
              {/* Elegant drag handle */}
              <div className="flex flex-col items-center pt-4 pb-2">
                <div className="h-1.5 w-12 rounded-full bg-gradient-to-r from-gray-300 to-gray-400 mb-4" />
                <h3 className="text-lg font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
                  Select Templates
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  Choose templates to generate forms
                </p>
              </div>

              {templates.length === 0 ? (
                <div className="px-6 py-12 text-center flex-1">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
                    <svg
                      className="w-8 h-8 text-gray-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-base text-gray-500 font-medium">
                    No templates available
                  </p>
                  <p className="text-sm text-gray-400 mt-2">
                    Templates will appear here when loaded
                  </p>
                </div>
              ) : (
                <>
                  {/* Scrollable template list */}
                  <div className="overflow-y-auto flex-1 max-h-[40vh] touch-pan-y scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
                    {templates.map((template, index) => {
                      const isGenerated = generatedTemplateIds.includes(
                        String(template.id)
                      );
                      const isChecked = selectedTemplateIds.includes(
                        String(template.id)
                      );

                      return (
                        <label
                          key={template.id}
                          className={`group relative flex items-center px-6 py-5 transition-all duration-200 ${
                            isGenerated
                              ? "cursor-not-allowed"
                              : "cursor-pointer active:bg-white/70"
                          }`}
                          style={{
                            animationDelay: `${index * 50}ms`,
                          }}
                        >
                          {/* Selection indicator */}
                          <div
                            className={`absolute left-0 top-0 bottom-0 w-1 transition-all duration-300 ${
                              isChecked && !isGenerated
                                ? "bg-gradient-to-b from-blue-500 to-purple-600"
                                : "bg-transparent"
                            }`}
                          />

                          {/* Custom checkbox */}
                          <div className="relative mr-4">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() =>
                                !isGenerated && onToggleTemplate(template.id)
                              }
                              disabled={isGenerated}
                              className="sr-only"
                            />
                            <div
                              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                                isGenerated
                                  ? "border-gray-300 bg-gray-100"
                                  : isChecked
                                  ? "border-blue-500 bg-gradient-to-r from-blue-500 to-purple-600 shadow-lg shadow-blue-500/25"
                                  : "border-gray-300 bg-white group-active:border-blue-400 group-active:shadow-sm"
                              }`}
                            >
                              {isChecked && !isGenerated && (
                                <svg
                                  className="w-4 h-4 text-white animate-in zoom-in duration-200"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="3"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                              {isGenerated && (
                                <svg
                                  className="w-4 h-4 text-gray-400"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M5 13l4 4L19 7"
                                  />
                                </svg>
                              )}
                            </div>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <div
                                className={`text-base font-semibold truncate transition-colors duration-200 ${
                                  isGenerated
                                    ? "text-gray-400"
                                    : "text-gray-900"
                                }`}
                              >
                                {template.name}
                              </div>
                              {isGenerated && (
                                <span className="ml-3 inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-gradient-to-r from-gray-100 to-gray-200 text-gray-500 animate-in slide-in-from-right duration-300">
                                  <svg
                                    className="w-3 h-3 mr-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                  Generated
                                </span>
                              )}
                            </div>
                            {template.description && (
                              <div
                                className={`text-sm truncate mt-2 transition-colors duration-200 ${
                                  isGenerated
                                    ? "text-gray-400"
                                    : "text-gray-500"
                                }`}
                              >
                                {template.description}
                              </div>
                            )}
                          </div>

                          {/* Hover glow effect */}
                          {!isGenerated && (
                            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/5 to-purple-600/5 opacity-0 group-active:opacity-100 transition-opacity duration-200" />
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {/* Generate Button */}
                  <GenerateButton
                    selectedTemplateIds={selectedTemplateIds}
                    onGenerate={handleGenerate}
                    disabled={disabled}
                  />
                </>
              )}
            </div>
          </>,
          document.body
        )}
    </div>
  );
}
